import * as React from 'react';
import Select, { SelectValue } from '../../../../Components/Base/Select/Select';
import {
	CaseTypeType,
	useLazyWorkplaceCaseTypesQuery,
	useWorkplaceCaseTypesQuery,
} from '../../../../graphql/graphql';
import { FieldError } from 'react-hook-form';
import { useEffect, useMemo, useState } from 'react';
import Loading from '../../../../Components/Loading';
import { useAppContext } from '../../../../Services/AppContext';
import useTranslation from '../../../../Hooks/useTranslation';
import { isString } from 'lodash';
import { useEffectAsync } from '../../../../helpers';

interface Props {
	onChange?: (value: SelectValue) => void;
	value?: SelectValue | string;
	error?: FieldError;
	workplaceId?: string;
	portal?: boolean;
	excludedIds?: string[];
	disabled?: boolean;
}

const WorkplaceOrganizationUnitSelect: React.FC<Props> = props => {
	const {
		value,
		onChange,
		workplaceId,
		portal = false,
		excludedIds,
		disabled = false,
	} = props;
	const { userData } = useAppContext();
	const { t } = useTranslation();
	const [options, setOptions] = useState<SelectValue[]>([]);
	const [isLoading, setIsLoading] = useState(false);
	const [getCaseTypes] = useLazyWorkplaceCaseTypesQuery();

	useEffectAsync(async () => {
		setIsLoading(true);
		const items: SelectValue[] = [];
		const { data } = await getCaseTypes({
			workplaceId: workplaceId ?? userData.workplaceId,
			caseTypeTypes: [CaseTypeType.Folder],
		});

		(data?.workplaceCaseTypes?.data || []).forEach(workplaceCaseType => {
			if (
				workplaceCaseType?.caseType?.id &&
				workplaceCaseType?.caseType?.label
			) {
				if (!excludedIds?.some(item => item === workplaceCaseType?.id)) {
					items.push({
						value: workplaceCaseType.caseType.id,
						label: workplaceCaseType.caseType.label,
					});
				}
			}
		});
		setOptions(items);
		setIsLoading(false);
	}, []);

	useEffect(() => {
		if (isString(value)) {
			const newValue = options.find(item => item.value === value);
			if (newValue) {
				onChange?.(newValue);
			}
		}
	}, [value, options]);

	if (isLoading) {
		return <Loading />;
	}

	return (
		<Select
			options={options}
			{...props}
			onChange={onChange}
			value={value}
			placeholder={t('base.shared.pick')}
			portal={portal}
			isClearable
			disabled={disabled}
		/>
	);
};

export default WorkplaceOrganizationUnitSelect;
